import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, exhaustMap, map, of } from "rxjs";
import { ApuStandardActions } from "@/core/store/apu-standard/apu-standard.actions";
import { ApuStandardService } from "@/core/services/apu-standard.service";
import { ApuStandardApiModel } from "@/core/models/api/apu-standard-api.model";

/**
 * Mensaje legible para el usuario a partir de un HttpErrorResponse. Los efectos NUNCA deben
 * tragarse el error con `of()`: quien despacha se queda esperando el *Success que nunca llega
 * (spinner colgado). Siempre se emite la acción *Fail correspondiente.
 */
function toErrorMessage(error: any): string {
    return error?.error?.message ?? error?.error?.title ?? error?.message ?? 'Ocurrió un error inesperado.';
}

@Injectable()
export class ApuStandardEffects {
    actions$ = inject(Actions);
    apuStandardService = inject(ApuStandardService);

    _loadApuStandard = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApuStandardActions.loadApuStandards),
            exhaustMap(() => {
                return this.apuStandardService.getAll().pipe(
                    map((response: any) => {
                        const apuStandards = response as ApuStandardApiModel[];
                        return ApuStandardActions.loadApuStandardSuccess({ apuStandards });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(ApuStandardActions.loadApuStandardFail({ error: toErrorMessage(error) }));
                    })
                )
            })
        )
    })

    _addApuStandard = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApuStandardActions.addApuStandard),
            exhaustMap((action) => {
                return this.apuStandardService.add(action.apuStandard).pipe(
                    map((response: any) => {
                        const apuStandard = response as ApuStandardApiModel;
                        return ApuStandardActions.addApuStandardSuccess({ apuStandard });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(ApuStandardActions.addApuStandardFail({ error: toErrorMessage(error) }));
                    })
                )
            })
        )
    })

    _updateApuStandard = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApuStandardActions.updateApuStandard),
            concatMap((action) => {
                return this.apuStandardService.update(action.apuStandard).pipe(
                    map((response: any) => {
                        // El PUT devuelve la entidad actualizada; si el backend respondiera vacío
                        // se conserva el modelo enviado para no romper el selectId del adapter.
                        const apuStandard = (response ?? action.apuStandard) as ApuStandardApiModel;
                        return ApuStandardActions.updateApuStandardSuccess({ apuStandard });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(ApuStandardActions.updateApuStandardFail({ error: toErrorMessage(error) }));
                    })
                )
            })
        )
    })

    _deleteApuStandard = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApuStandardActions.deleteApuStandard),
            concatMap((action) => {
                return this.apuStandardService.delete(action.apuStandardId).pipe(
                    map(() => ApuStandardActions.deleteApuStandardSuccess({ apuStandardId: action.apuStandardId })),
                    catchError((error) => {
                        console.error(error);
                        return of(ApuStandardActions.deleteApuStandardFail({ error: toErrorMessage(error) }));
                    })
                )
            })
        )
    })

    _loadApuStandardById = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApuStandardActions.loadApuStandardById),
            exhaustMap((action) => {
                return this.apuStandardService.getById(action.apuStandardId).pipe(
                    map((response: any) => {
                        const apuStandard = response as ApuStandardApiModel;
                        return ApuStandardActions.loadApuStandardByIdSuccess({ apuStandard });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(ApuStandardActions.loadApuStandardByIdFail({ error: toErrorMessage(error) }));
                    })
                )
            })
        )
    })
}
